import { useState, useEffect } from 'react';
import { Calendar, TrendingUp, AlertCircle } from 'lucide-react';

interface MonthlyConsumptionInputProps {
  initialValues?: number[];
  onValuesChange: (values: number[]) => void;
  yearlyTotal?: number;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function MonthlyConsumptionInput({ 
  initialValues, 
  onValuesChange,
  yearlyTotal 
}: MonthlyConsumptionInputProps) {
  const [monthlyValues, setMonthlyValues] = useState<number[]>(
    initialValues || new Array(12).fill(0)
  );
  const [showHelper, setShowHelper] = useState(false);

  useEffect(() => {
    if (initialValues && initialValues.length === 12) {
      setMonthlyValues(initialValues);
    }
  }, [initialValues]);

  const handleValueChange = (month: number, value: string) => {
    const numValue = parseFloat(value) || 0;
    // Validate: consumption should be non-negative and reasonable (0-100,000 kWh/month)
    if (numValue >= 0 && numValue <= 100000) {
      const newValues = [...monthlyValues];
      newValues[month] = numValue;
      setMonthlyValues(newValues);
      onValuesChange(newValues);
    }
  };

  const fillWithAverage = () => {
    if (yearlyTotal && yearlyTotal > 0) {
      const avgValue = Math.round(yearlyTotal / 12);
      const newValues = new Array(12).fill(avgValue);
      setMonthlyValues(newValues);
      onValuesChange(newValues);
    }
  };

  const fillWithPattern = () => {
    // Saudi Arabia consumption pattern (higher in summer due to AC)
    // Fixed default kWh values (Jan → Dec) so this works even when no yearly total is provided.
    const newValues = [800, 900, 1000, 1200, 1400, 1600, 1800, 1600, 1400, 1200, 1000, 900];
    setMonthlyValues(newValues);
    onValuesChange(newValues);
  };

  const calculateTotal = () => {
    return monthlyValues.reduce((sum, val) => sum + val, 0);
  };

  const total = calculateTotal();
  const hasYearlyMismatch = yearlyTotal && Math.abs(total - yearlyTotal) > yearlyTotal * 0.05;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Calendar className="h-5 w-5 text-blue-600" />
          <label className="block text-sm font-medium text-gray-700">
            Monthly Consumption Breakdown
          </label>
        </div>
        <button
          type="button"
          onClick={() => setShowHelper(!showHelper)}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          {showHelper ? 'Hide' : 'Show'} Helper
        </button>
      </div>

      {showHelper && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <p className="text-sm text-gray-700">
            Enter your monthly electricity consumption for more accurate system sizing:
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={fillWithAverage}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50"
              disabled={!yearlyTotal}
            >
              Fill with Average
            </button>
            <button
              type="button"
              onClick={fillWithPattern}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50"
            >
              Fill with Saudi Pattern
            </button>
          </div>
          {!yearlyTotal && (
            <p className="text-xs text-gray-500">
              Tip: “Fill with Average” needs a yearly total. “Fill with Saudi Pattern” works anytime.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {MONTH_NAMES.map((month, index) => (
          <div key={month} className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">
              {month}
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                step="1"
                value={monthlyValues[index] || ''}
                onChange={(e) => handleValueChange(index, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
              />
              <span className="absolute right-3 top-2 text-xs text-gray-400">
                kWh
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <TrendingUp className="h-5 w-5 text-green-600" />
          <span className="text-sm font-medium text-gray-700">
            Total Annual Consumption:
          </span>
        </div>
        <span className="text-lg font-bold text-gray-900">
          {total.toLocaleString()} kWh
        </span>
      </div>

      {hasYearlyMismatch && (
        <div className="flex items-start space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <strong>Note:</strong> Monthly total ({total.toLocaleString()} kWh) 
            differs from yearly consumption ({yearlyTotal?.toLocaleString()} kWh). 
            We'll use the monthly breakdown for more accurate calculations.
          </div>
        </div>
      )}
    </div>
  );
}
